import React from 'react';
import { useQuery, useMutation } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { ShopifyInventoryWebhook } from '@/types';

const ShopifyWebhooks: React.FC = () => {
  const {
    data: webhooks = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<ShopifyInventoryWebhook[]>(
    'shopifyInventoryWebhooks',
    apiService.getShopifyInventoryWebhooks
  );

  const createMutation = useMutation(
    () => apiService.createShopifyInventoryWebhook(),
    {
      onSuccess: async () => {
        await refetch();
        alert('Inventory webhook created');
      },
      onError: (error: any) => {
        console.error('Failed to create webhook:', error);
        alert('Failed to create webhook');
      },
    }
  );

  const clearMutation = useMutation(
    () => apiService.clearShopifyInventoryWebhooks(),
    {
      onSuccess: async () => {
        await refetch();
        alert('Inventory webhooks cleared');
      },
      onError: (error: any) => {
        console.error('Failed to clear webhooks:', error);
        alert('Failed to clear webhooks');
      },
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <p className="text-red-600">Failed to load Shopify inventory webhooks.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">ShopifyWebhooks</h1>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Callback Url</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {webhooks.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center text-gray-500 py-4">
                    No webhooks found
                  </td>
                </tr>
              ) : (
                webhooks.map((wh) => (
                  <tr key={String(wh.id)}>
                    <td className="font-mono text-sm">{wh.id}</td>
                    <td className="font-mono text-sm">{wh.callback_url}</td>
                    <td className="font-mono text-sm">{wh.created_at}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-start pt-6 space-x-3">
          <button
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isLoading}
            className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
            style={{ backgroundColor: 'hsl(210 100% 45%)' }}
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
            <span>{createMutation.isLoading ? 'Creating...' : 'Create Wekbhook'}</span>
          </button>

          <button
            type="button"
            onClick={() => clearMutation.mutate()}
            disabled={clearMutation.isLoading}
            className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
            style={{ backgroundColor: 'hsl(0 70% 45%)' }}
          >
            {clearMutation.isLoading ? 'Clearing...' : 'Clear webhooks'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopifyWebhooks;

